import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEdit } from "react-icons/fa";
import { CalendarPlus, Plus, Trash2 } from "lucide-react";

import styles from "./Employees.module.scss";
import Spinner from "../../components/spinner";
import Select from "../../components/select";
import { SegmentedToggle } from "../../components/editPageHeader";
import { useConfirm } from "../../components/confirmDialog";
import { useLanguage } from "../../context/languageContext";
import { Can } from "../../context/permissionContext";
import EmployeePopup from "./employeePopup";
import LeavePopup from "./leavePopup";

const API = process.env.REACT_APP_API_ROOT;

const LEAVE_LABELS = { sick: "Sick", festival: "Festival" };

// The panel's own month, not UTC's — just after midnight in India, UTC is
// still on yesterday, and on the 1st that is last month.
const thisMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** "2026-10-20" → "20 Oct 2026", read as a calendar day (no time zone shift). */
const formatDay = (day) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });

/**
 * Employees and their leave. Two tabs over one page: the people (with this
 * year's sick / festival day counts), and the leaves marked in a month.
 */
function Employees() {
    const { adminInfo } = useLanguage();
    const confirm = useConfirm();

    const [tab, setTab] = useState("employees");
    const [employees, setEmployees] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [loadingLeaves, setLoadingLeaves] = useState(false);
    const [month, setMonth] = useState(thisMonth);
    const [employeeFilter, setEmployeeFilter] = useState(null);

    // false = closed, null = create, object = edit that employee.
    const [editingEmployee, setEditingEmployee] = useState(false);
    // false = closed, otherwise the employeeId to preselect (or null).
    const [markingFor, setMarkingFor] = useState(false);

    const headers = useMemo(() => ({ Authorization: `Bearer ${adminInfo?.token}` }), [adminInfo]);
    // The panel's year, sent rather than left to the server: just after
    // midnight on 1 January IST, the server's UTC year is still the old one.
    const year = new Date().getFullYear();
    // Only the latest leaves request may write the table — stepping through
    // months quickly would otherwise let a slow, older reply land last.
    const leavesRequest = useRef(0);

    const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
            const { data: response } = await axios.get(`${API}/api/admin/employees/getall`, { headers, params: { year } });
            if (response.status) setEmployees(response.data || []);
            else toast.error(response.message || "Could not load employees.");
        } catch (error) {
            console.error("fetchEmployees catch error::", error);
            toast.error(error.response?.data?.message || error.response?.data?.error || "Could not load employees.");
        } finally {
            setLoadingEmployees(false);
        }
    };

    const fetchLeaves = async () => {
        const request = ++leavesRequest.current;
        setLoadingLeaves(true);
        try {
            const params = { month };
            if (employeeFilter) params.employeeId = employeeFilter.value;
            const { data: response } = await axios.get(`${API}/api/admin/employees/leave/getall`, { headers, params });
            if (request !== leavesRequest.current) return;
            if (response.status) setLeaves(response.data || []);
            else toast.error(response.message || "Could not load leaves.");
        } catch (error) {
            if (request !== leavesRequest.current) return;
            console.error("fetchLeaves catch error::", error);
            toast.error(error.response?.data?.message || error.response?.data?.error || "Could not load leaves.");
        } finally {
            if (request === leavesRequest.current) setLoadingLeaves(false);
        }
    };

    useEffect(() => {
        if (adminInfo?.token) fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    useEffect(() => {
        if (adminInfo?.token && month) fetchLeaves();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo, month, employeeFilter]);

    // A new leave changes both tabs: the month's list and the year's counts.
    const refreshAll = () => {
        fetchEmployees();
        fetchLeaves();
    };

    const handleDeleteEmployee = async (employee) => {
        const ok = await confirm({
            title: `Remove ${employee.name}?`,
            body: "They will no longer be listed. Leaves already marked for them are kept.",
            confirmLabel: "Remove",
        });
        if (!ok) return;
        try {
            const { data: response } = await axios.put(`${API}/api/admin/employees/delete/${employee.employeeId}`, {}, { headers });
            if (response.status) {
                toast.success(response.message || "Employee removed.");
                // A filter on someone no longer listed can't be seen or cleared
                // from the picker, so it goes with them.
                if (employeeFilter?.value === employee.employeeId) {
                    setEmployeeFilter(null);
                    fetchEmployees();
                } else {
                    refreshAll();
                }
            } else {
                toast.error(response.message || "Could not remove the employee.");
            }
        } catch (error) {
            console.error("handleDeleteEmployee catch error::", error);
            toast.error(error.response?.data?.message || error.response?.data?.error || "Could not remove the employee.");
        }
    };

    const handleDeleteLeave = async (leave) => {
        const ok = await confirm({
            title: "Delete this leave?",
            quote: `${leave.employeeName} — ${LEAVE_LABELS[leave.type]}, ${formatDay(leave.fromDate)}${leave.days > 1 ? ` to ${formatDay(leave.toDate)}` : ""}`,
            confirmLabel: "Delete",
        });
        if (!ok) return;
        try {
            const { data: response } = await axios.put(`${API}/api/admin/employees/leave/delete/${leave.leaveId}`, {}, { headers });
            if (response.status) {
                toast.success(response.message || "Leave removed.");
                refreshAll();
            } else {
                toast.error(response.message || "Could not delete the leave.");
            }
        } catch (error) {
            console.error("handleDeleteLeave catch error::", error);
            toast.error(error.response?.data?.message || error.response?.data?.error || "Could not delete the leave.");
        }
    };

    const employeeOptions = employees.map((e) => ({ value: e.employeeId, label: e.name }));

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">Employees</h4>
                    <small className="text-muted">Mark sick and festival leave for the team.</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <SegmentedToggle
                        value={tab}
                        onChange={setTab}
                        options={[
                            { value: "employees", label: `Employees (${employees.length})` },
                            { value: "leaves", label: "Leaves" },
                        ]}
                    />
                    <Can feature="employees" action="create">
                        {tab === "employees" ? (
                            <button type="button" className="btn btn-dark d-flex align-items-center gap-2" onClick={() => setEditingEmployee(null)}>
                                <Plus size={18} /> Add Employee
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-dark d-flex align-items-center gap-2"
                                onClick={() => setMarkingFor(employeeFilter?.value ?? null)}
                                disabled={employees.length === 0}
                                title={employees.length === 0 ? "Add an employee first" : undefined}
                            >
                                <CalendarPlus size={18} /> Mark Leave
                            </button>
                        )}
                    </Can>
                </div>
            </div>

            {tab === "employees" ? (
                loadingEmployees ? (
                    <Spinner />
                ) : (
                    <div className={`${styles.container} table-responsive mt-1`}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.index}>S.no</th>
                                    <th>Name</th>
                                    <th>Designation</th>
                                    <th>Email</th>
                                    <th className={styles.num}>Sick ({year})</th>
                                    <th className={styles.num}>Festival ({year})</th>
                                    <th className={styles.action}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className={styles.empty}>No employees yet. Add one to get started.</td>
                                    </tr>
                                ) : (
                                    employees.map((employee, index) => (
                                        <tr key={employee.employeeId}>
                                            <td className={styles.index}>{index + 1}</td>
                                            <td className={styles.name}>{employee.name}</td>
                                            <td>{employee.designation || <span className={styles.none}>—</span>}</td>
                                            <td>{employee.email || <span className={styles.none}>—</span>}</td>
                                            <td className={styles.num}>{employee.leaveDays?.sick ?? 0}</td>
                                            <td className={styles.num}>{employee.leaveDays?.festival ?? 0}</td>
                                            <td className={styles.action}>
                                                <div className="d-inline-flex align-items-center gap-1">
                                                    <Can feature="employees" action="create">
                                                        <div title="Mark leave" onClick={() => setMarkingFor(employee.employeeId)}>
                                                            <CalendarPlus size={20} />
                                                        </div>
                                                    </Can>
                                                    <Can feature="employees" action="edit">
                                                        <div title="Edit" onClick={() => setEditingEmployee(employee)}>
                                                            <FaEdit size={20} />
                                                        </div>
                                                    </Can>
                                                    <Can feature="employees" action="delete">
                                                        <div title="Remove employee" className={styles.deleteAction} onClick={() => handleDeleteEmployee(employee)}>
                                                            <Trash2 size={20} />
                                                        </div>
                                                    </Can>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                <>
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <input
                            type="month"
                            className={`form_control ${styles.monthInput}`}
                            value={month}
                            onChange={(e) => setMonth(e.target.value || thisMonth())}
                            aria-label="Month"
                        />
                        <div className={styles.employeeFilter}>
                            <Select
                                isClearable
                                placeholder="All employees"
                                value={employeeFilter}
                                onChange={setEmployeeFilter}
                                options={employeeOptions}
                            />
                        </div>
                    </div>
                    {loadingLeaves ? (
                        <Spinner />
                    ) : (
                        <div className={`${styles.container} table-responsive`}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th className={styles.index}>S.no</th>
                                        <th>Employee</th>
                                        <th>Type</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th className={styles.num}>Days</th>
                                        <th>Note</th>
                                        <th className={styles.action}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaves.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className={styles.empty}>No leave marked this month.</td>
                                        </tr>
                                    ) : (
                                        leaves.map((leave, index) => (
                                            <tr key={leave.leaveId}>
                                                <td className={styles.index}>{index + 1}</td>
                                                <td className={styles.name}>{leave.employeeName || <span className={styles.none}>—</span>}</td>
                                                <td>
                                                    <span className={`${styles.pill} ${styles[leave.type]}`}>{LEAVE_LABELS[leave.type]}</span>
                                                </td>
                                                <td>{formatDay(leave.fromDate)}</td>
                                                <td>{formatDay(leave.toDate)}</td>
                                                <td className={styles.num}>{leave.days}</td>
                                                <td className={styles.note}>{leave.note || <span className={styles.none}>—</span>}</td>
                                                <td className={styles.action}>
                                                    <Can feature="employees" action="delete">
                                                        <div title="Delete leave" className={styles.deleteAction} onClick={() => handleDeleteLeave(leave)}>
                                                            <Trash2 size={20} />
                                                        </div>
                                                    </Can>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {editingEmployee !== false && (
                <EmployeePopup
                    key={editingEmployee?.employeeId ?? "new"}
                    employee={editingEmployee}
                    handleCloseModal={() => setEditingEmployee(false)}
                    onSuccess={refreshAll}
                />
            )}

            {markingFor !== false && (
                <LeavePopup
                    employeeOptions={employeeOptions}
                    employeeId={markingFor}
                    handleCloseModal={() => setMarkingFor(false)}
                    onSuccess={refreshAll}
                />
            )}
        </React.Fragment>
    );
}

export default Employees;
